import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DollarSign,
    Plus,
    Download,
    Search,
    Filter,
    Trash2,
    User,
    CreditCard,
    UserCheck,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    Wallet,
    Edit2,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import {
    ButtonPrimary,
    ButtonSecondary,
    Card,
    Badge,
    Modal,
    Input,
    Alert,
    Spinner,
    StatCard
} from '../../components/ComponentLibrary';
import {
    getPayments,
    createPayment,
    deletePayment,
    updatePayment,
    getFinancialSummary,
    getStudentsFinancialStatus,
    updateStudentFees,
    getCollectorSummary,
    createCollectorTransaction,
    getCollectorHistory,
    updateCollectorTransaction,
    deleteCollectorTransaction
} from '../../services/api';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


export default function AdminFinance() {
    const navigate = useNavigate();
    // State management
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState({
        totalCollected: 0,
        abdullahCollected: 0,
        mohamedCollected: 0,
        totalPending: 0
    });
    const [collectorWallet, setCollectorWallet] = useState({
        abdullah: { totalIncoming: 0, totalOutgoing: 0, balance: 0 },
        mohamed: { totalIncoming: 0, totalOutgoing: 0, balance: 0 }
    });
    const [studentsStatus, setStudentsStatus] = useState([]);
    const [outgoingTransactions, setOutgoingTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Confirm Modal & Alert state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const CW_showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    // Pagination state for Transaction Ledger
    const [ledgerPage, setLedgerPage] = useState(1);
    const ledgerPageSize = 6;

    // Pagination state for Student Standings
    const [studentsPage, setStudentsPage] = useState(1);
    const studentsPageSize = 6;

    // Modal state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isFeesModalOpen, setIsFeesModalOpen] = useState(false);
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);

    // Form state
    const [walletForm, setWalletForm] = useState({
        collectorName: 'Abdullah',
        type: 'Outgoing',
        amount: '',
        notes: '',
        category: 'Expense'
    });

    const [isEditingWallet, setIsEditingWallet] = useState(false);
    const [editingWalletId, setEditingWalletId] = useState(null);

    const [newPayment, setNewPayment] = useState({
        studentId: '',
        amount: '',
        paymentMethod: 'VodafoneCash',
        receivedBy: 'Abdullah',
        notes: '',
        date: new Date().toISOString().split('T')[0]
    });

    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null);

    const [feesData, setFeesData] = useState({
        studentId: '',
        fullName: '',
        totalFees: ''
    });

    // Filters state
    const [filters, setFilters] = useState({
        search: '',
        receivedBy: '',
        paymentMethod: ''
    });

    // Load data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [paymentsData, summaryData, studentsData, walletData, historyData] = await Promise.all([
                getPayments(),
                getFinancialSummary(),
                getStudentsFinancialStatus(),
                getCollectorSummary(),
                getCollectorHistory({ type: 'Outgoing' })
            ]);

            setPayments(paymentsData.data);
            setSummary(summaryData);
            setStudentsStatus(studentsData.data);
            setCollectorWallet(walletData.data);
            setOutgoingTransactions(historyData.data || []);
            setError(null);
        } catch (err) {
            console.error('Finance page data fetch error:', err);
            setError('Failed to load financial data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handlers
    const handleAddPayment = async (e) => {
        e.preventDefault();
        try {
            if (!newPayment.studentId || !newPayment.amount) {
                CW_showAlert('Missing Information', 'Please fill in all required fields (Student and Amount)', 'warning');
                return;
            }


            if (isEditingPayment) {
                // We need an updatePayment service or use createPayment logic
                // For now, I'll use the existing updatePayment if it exists in API
                await updatePayment(editingPaymentId, newPayment);
            } else {
                await createPayment(newPayment);
            }

            setIsAddModalOpen(false);
            setIsEditingPayment(false);
            setEditingPaymentId(null);
            setNewPayment({
                studentId: '',
                amount: '',
                paymentMethod: 'VodafoneCash',
                receivedBy: 'Abdullah',
                notes: '',
                date: new Date().toISOString().split('T')[0]
            });
            fetchData();
        } catch (err) {
            CW_showAlert('Save Failed', err.message || 'Failed to save payment', 'danger');
        }
    };


    const startEditPayment = (p) => {
        setNewPayment({
            studentId: p.studentId?._id,
            amount: p.amount,
            paymentMethod: p.paymentMethod,
            receivedBy: p.receivedBy,
            notes: p.notes,
            date: new Date(p.date).toISOString().split('T')[0],
            installmentNumber: p.installmentNumber
        });
        setEditingPaymentId(p._id);
        setIsEditingPayment(true);
        setIsAddModalOpen(true);
    };

    const handleDeletePayment = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Payment?',
            message: 'Are you sure you want to delete this payment record? This will update the student balance accordingly and cannot be undone.',
            type: 'danger',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await deletePayment(id);
                    fetchData();
                    CW_showAlert('Success', 'Payment deleted successfully', 'info');
                } catch (err) {
                    CW_showAlert('Deletion Failed', 'Failed to delete payment', 'danger');
                }
            }
        });
    };


    const handleUpdateFees = async (e) => {
        e.preventDefault();
        try {
            await updateStudentFees(feesData.studentId, feesData.totalFees);
            setIsFeesModalOpen(false);
            fetchData();
        } catch (err) {
            CW_showAlert('Update Failed', 'Failed to update fees', 'danger');
        }
    };


    const handleWalletTransaction = async (e) => {
        e.preventDefault();
        try {
            if (isEditingWallet) {
                await updateCollectorTransaction(editingWalletId, walletForm);
            } else {
                await createCollectorTransaction(walletForm);
            }

            setIsWalletModalOpen(false);
            setIsEditingWallet(false);
            setEditingWalletId(null);
            setWalletForm({
                collectorName: 'Abdullah',
                type: 'Outgoing',
                amount: '',
                notes: '',
                category: 'Expense'
            });
            fetchData();
        } catch (err) {
            CW_showAlert('Transaction Failed', err.message || 'Failed to save transaction', 'danger');
        }
    };


    const handleDeleteWalletTransaction = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Transaction?',
            message: 'Delete this transaction? This will restore the collector balance accordingly.',
            type: 'danger',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await deleteCollectorTransaction(id);
                    fetchData();
                    CW_showAlert('Success', 'Transaction deleted successfully', 'info');
                } catch (err) {
                    CW_showAlert('Deletion Failed', err.message || 'Failed to delete transaction', 'danger');
                }
            }
        });
    };


    const startEditWallet = (t) => {
        setWalletForm({
            collectorName: t.collectorName,
            type: t.type,
            amount: t.amount,
            notes: t.notes,
            category: t.category
        });
        setEditingWalletId(t._id);
        setIsEditingWallet(true);
        setIsWalletModalOpen(true);
    };

    const exportToExcel = (data, fileName) => {
        if (data.length === 0) return;

        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','),
            ...data.map(row => headers.map(header => {
                const cell = row[header] === null || row[header] === undefined ? '' : row[header];
                return `"${String(cell).replace(/"/g, '""')}"`;
            }).join(','))
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${fileName}_${new Date().toLocaleDateString()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filtered data
    const filteredPayments = useMemo(() => {
        return payments.filter(p => {
            const matchesSearch = p.studentId?.fullName.toLowerCase().includes(filters.search.toLowerCase()) ||
                p.notes?.toLowerCase().includes(filters.search.toLowerCase());
            const matchesReceiver = !filters.receivedBy || p.receivedBy === filters.receivedBy;
            const matchesMethod = !filters.paymentMethod || p.paymentMethod === filters.paymentMethod;
            return matchesSearch && matchesReceiver && matchesMethod;
        });
    }, [payments, filters]);

    // Paginated payments for display
    const totalLedgerPages = Math.ceil(filteredPayments.length / ledgerPageSize);
    const paginatedPayments = useMemo(() => {
        const start = (ledgerPage - 1) * ledgerPageSize;
        return filteredPayments.slice(start, start + ledgerPageSize);
    }, [filteredPayments, ledgerPage]);

    // Reset page when filters change
    useEffect(() => {
        setLedgerPage(1);
    }, [filters]);

    // Paginated students for display
    const totalStudentsPages = Math.ceil(studentsStatus.length / studentsPageSize);
    const paginatedStudents = useMemo(() => {
        const start = (studentsPage - 1) * studentsPageSize;
        return studentsStatus.slice(start, start + studentsPageSize);
    }, [studentsStatus, studentsPage]);

    if (loading && payments.length === 0) {
        return (
            <div className="admin-loading-state">
                <Spinner size="lg" />
                <p>Establishing secure financial connection...</p>
            </div>
        );
    }

    return (
        <div className="finance-management-page">
            {/* Header Actions */}
            <div className="admin-page-header">
                <div className="header-text">
                    <h2 className="finance-main-title">💰 Financial Operations</h2>
                    <p className="finance-subtitle">Monitor collections, manage student balances and track revenue streams.</p>
                </div>
                <div className="header-actions-group">
                    <ButtonSecondary onClick={() => exportToExcel(payments.map(p => ({
                        Date: new Date(p.date).toLocaleDateString(),
                        Student: p.studentId?.fullName,
                        Amount: p.amount,
                        Method: p.paymentMethod,
                        Collector: p.receivedBy,
                        Notes: p.notes
                    })), 'CodeWave_Payments')}>
                        <Download size={18} className="mr-2" /> Export CSV
                    </ButtonSecondary>
                    <ButtonPrimary onClick={() => setIsAddModalOpen(true)}>
                        <Plus size={18} className="mr-2" /> Add New Payment
                    </ButtonPrimary>
                </div>
            </div>

            {error && <Alert variant="error" className="mb-6">{error}</Alert>}

            {/* Summary Cards */}
            <div className="finance-summary-grid">
                <StatCard
                    title="Total Revenue"
                    value={`${summary?.totalCollected || 0} EGP`}
                    icon="rocket"
                    variant="primary"
                    description="Gross student payments"
                />
                <StatCard
                    title="Abdullah's Balance"
                    value={`${collectorWallet?.abdullah?.balance || 0} EGP`}
                    icon="wallet"
                    variant="ocean"
                    description={`In: ${collectorWallet?.abdullah?.totalIncoming || 0} | Out: ${collectorWallet?.abdullah?.totalOutgoing || 0}`}
                />
                <StatCard
                    title="Mohamed's Balance"
                    value={`${collectorWallet?.mohamed?.balance || 0} EGP`}
                    icon="wallet"
                    variant="indigo"
                    description={`In: ${collectorWallet?.mohamed?.totalIncoming || 0} | Out: ${collectorWallet?.mohamed?.totalOutgoing || 0}`}
                />
                <StatCard
                    title="Outstanding"
                    value={`${summary?.totalPending || 0} EGP`}
                    icon="target"
                    variant="error"
                    description="Uncollected fees"
                />
            </div>

            {/* Wallet Quick Actions */}
            <div className="wallet-actions-banner mt-6 mb-10">
                <Card className="wallet-banner-card" hoverable={false}>
                    <div className="banner-content">
                        <div className="banner-text">
                            <h3>💳 Wallet Management</h3>
                            <p>Track collector expenses and transfers to main treasury.</p>
                        </div>
                        <ButtonSecondary onClick={() => {
                            setIsEditingWallet(false);
                            setIsWalletModalOpen(true);
                        }}>
                            <Plus size={18} className="mr-2" /> Add Expense / Transfer
                        </ButtonSecondary>
                    </div>
                </Card>
            </div>

            {/* Main Content Sections */}
            <div className="finance-content-grid">

                {/* Section 1: All Payments Table */}
                <Card className="finance-card payments-section" hoverable={false}>
                    <div className="card-header-flex">
                        <div className="card-title-group">
                            <div className="title-icon-wrapper">
                                <Wallet size={20} />
                            </div>
                            <div>
                                <h3>Transaction Ledger</h3>
                                <p>Record of all verified incoming payments</p>
                            </div>
                        </div>
                        <div className="table-filters">
                            <div className="search-box">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search by student..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                />
                            </div>
                            <select
                                value={filters.receivedBy}
                                onChange={(e) => setFilters({ ...filters, receivedBy: e.target.value })}
                                className="filter-select"
                            >
                                <option value="">All Collectors</option>
                                <option value="Abdullah">Abdullah</option>
                                <option value="Mohamed">Mohamed</option>
                            </select>
                        </div>
                    </div>

                    <div className="finance-table-wrapper">
                        <table className="finance-data-table">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>Received By</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedPayments.length > 0 ? paginatedPayments.map(p => (
                                    <tr key={p._id}>
                                        <td>
                                            <div className="student-info-cell">
                                                <div
                                                    className="student-avatar-sm"
                                                    onClick={() => navigate(`/admin/finance/student/${p.studentId?._id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {p.studentId?.fullName ? p.studentId.fullName[0] : '?'}
                                                </div>
                                                <span
                                                    className="clickable-student-name"
                                                    onClick={() => navigate(`/admin/finance/student/${p.studentId?._id}`)}
                                                >
                                                    {p.studentId?.fullName || 'Deleted Student'}
                                                </span>
                                            </div>
                                        </td>
                                        <td><span className="amount-text">{p.amount} EGP</span></td>
                                        <td>
                                            <Badge variant={p.paymentMethod === 'Cash' ? 'sun' : 'ocean'} size="sm">
                                                {p.paymentMethod}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="collector-badge">
                                                {p.receivedBy}
                                            </div>
                                        </td>
                                        <td><span className="date-text">{new Date(p.date).toLocaleDateString()}</span></td>
                                        <td>
                                            <div className="table-actions-row">
                                                <button
                                                    className="table-action-btn edit"
                                                    onClick={() => startEditPayment(p)}
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    className="table-action-btn delete"
                                                    onClick={() => handleDeletePayment(p._id)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="empty-row">No transactions found matching your filters.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalLedgerPages > 1 && (
                        <div className="table-pagination-footer">
                            <div className="pagination-info">
                                Page <strong>{ledgerPage}</strong> of <strong>{totalLedgerPages}</strong>
                            </div>
                            <div className="pagination-btns">
                                <button
                                    className="pagination-btn"
                                    onClick={() => setLedgerPage(prev => Math.max(1, prev - 1))}
                                    disabled={ledgerPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    className="pagination-btn"
                                    onClick={() => setLedgerPage(prev => Math.min(totalLedgerPages, prev + 1))}
                                    disabled={ledgerPage === totalLedgerPages}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Section 2: Student Financial Standings */}
                <Card className="finance-card standings-section" hoverable={false}>
                    <div className="card-header-flex">
                        <div className="card-title-group">
                            <div className="title-icon-wrapper">
                                <UserCheck size={20} />
                            </div>
                            <div>
                                <h3>Student Standings</h3>
                                <p>Financial health and balance status</p>
                            </div>
                        </div>
                    </div>

                    <div className="standings-table-container">
                        <table className="standings-table">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th className="amount-col">Fees</th>
                                    <th className="amount-col">Remaining</th>
                                    <th className="status-col">Status</th>
                                    <th className="actions-col"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedStudents.length > 0 ? paginatedStudents.map(s => (
                                    <tr key={s._id}>
                                        <td>
                                            <div className="student-avatar-cell">
                                                <div className="avatar-circle">
                                                    {s.fullName?.[0] || '?'}
                                                </div>
                                                <div className="student-meta">
                                                    <span
                                                        className="st-name"
                                                        onClick={() => navigate(`/admin/finance/student/${s._id}`)}
                                                    >
                                                        {s.fullName}
                                                    </span>
                                                    <span className="st-track">{s.track || 'Unassigned'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="amount-col">
                                            <span className="amount-label">{(s.totalFees || 0).toLocaleString()}</span>
                                        </td>
                                        <td className="amount-col">
                                            <span className={`amount-label ${(s.remainingAmount || 0) > 0 ? 'text-danger' : 'text-success'}`}>
                                                {(s.remainingAmount || 0).toLocaleString()}
                                            </span>
                                        </td>
                                        <td className="status-col">
                                            <span className={`standings-badge ${(s.paymentStatus || 'unpaid').toLowerCase()}`}>
                                                {s.paymentStatus || 'Unpaid'}
                                            </span>
                                        </td>
                                        <td className="actions-col">
                                            <button
                                                className="adjust-btn"
                                                onClick={() => {
                                                    setFeesData({
                                                        studentId: s._id,
                                                        fullName: s.fullName,
                                                        totalFees: s.totalFees
                                                    });
                                                    setIsFeesModalOpen(true);
                                                }}
                                            >
                                                Adjust
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="5" className="empty-row text-center py-8 text-slate-400">
                                            No student records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls for Students */}
                    {totalStudentsPages > 1 && (
                        <div className="table-pagination-footer mt-auto pt-4">
                            <div className="pagination-info">
                                Page <strong>{studentsPage}</strong> of <strong>{totalStudentsPages}</strong>
                            </div>
                            <div className="pagination-btns">
                                <button
                                    className="pagination-btn"
                                    onClick={() => setStudentsPage(prev => Math.max(1, prev - 1))}
                                    disabled={studentsPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    className="pagination-btn"
                                    onClick={() => setStudentsPage(prev => Math.min(totalStudentsPages, prev + 1))}
                                    disabled={studentsPage === totalStudentsPages}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            {/* Section 3: Outgoing Transactions (Expenses/Transfers) */}
            <div className="finance-full-width mt-8">
                <Card className="finance-card outgoing-section" hoverable={false}>
                    <div className="card-header-flex">
                        <div className="card-title-group">
                            <div className="title-icon-wrapper">
                                <ArrowDownRight className="text-danger" size={20} />
                            </div>
                            <div>
                                <h3>Outgoing Transactions</h3>
                                <p>Record of expenses, spending, and treasury transfers</p>
                            </div>
                        </div>
                    </div>

                    <div className="finance-table-wrapper">
                        <table className="finance-data-table">
                            <thead>
                                <tr>
                                    <th>Collector</th>
                                    <th>Amount</th>
                                    <th>Category</th>
                                    <th>Notes</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {outgoingTransactions.length > 0 ? outgoingTransactions.map(t => (
                                    <tr key={t._id}>
                                        <td>
                                            <div className="collector-info-cell">
                                                <div className={`collector-mini-avatar ${t.collectorName.toLowerCase()}`}>
                                                    {t.collectorName[0]}
                                                </div>
                                                <span>{t.collectorName}</span>
                                            </div>
                                        </td>
                                        <td><span className="amount-text text-danger">-{t.amount} EGP</span></td>
                                        <td>
                                            <Badge variant={t.category === 'Transfer' ? 'ocean' : 'sun'} size="sm">
                                                {t.category}
                                            </Badge>
                                        </td>
                                        <td className="notes-cell">{t.notes || '---'}</td>
                                        <td><span className="date-text">{new Date(t.createdAt).toLocaleDateString()}</span></td>
                                        <td>
                                            <div className="table-actions-row">
                                                <button className="table-action-btn edit" onClick={() => startEditWallet(t)}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <button className="table-action-btn delete" onClick={() => handleDeleteWalletTransaction(t._id)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="empty-row">No outgoing transactions recorded yet.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            {/* Modals */}
            <Modal
                isOpen={isAddModalOpen}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setIsEditingPayment(false);
                    setEditingPaymentId(null);
                }}
                title={isEditingPayment ? "📝 Edit Payment Record" : "➕ Register New Payment"}
                size="md"
            >
                <form onSubmit={handleAddPayment} className="finance-modal-form">
                    <div className="form-grid">
                        <div className="full-width">
                            <label className="cw-input-label">Select Student *</label>
                            <select
                                className="cw-input"
                                value={newPayment.studentId}
                                onChange={(e) => setNewPayment({ ...newPayment, studentId: e.target.value })}
                                required
                            >
                                <option value="">-- Choose Student --</option>
                                {studentsStatus.map(s => (
                                    <option key={s._id} value={s._id}>{s.fullName} ({s.track})</option>
                                ))}
                            </select>
                        </div>

                        <Input
                            label="Amount (EGP) *"
                            type="number"
                            placeholder="0.00"
                            value={newPayment.amount}
                            onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                            required
                        />

                        <Input
                            label="Installment #"
                            type="number"
                            placeholder="e.g. 1"
                            value={newPayment.installmentNumber || ''}
                            onChange={(e) => setNewPayment({ ...newPayment, installmentNumber: e.target.value })}
                        />

                        <div className="form-group">
                            <label className="cw-input-label">Payment Date</label>
                            <Input
                                type="date"
                                value={newPayment.date}
                                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label className="cw-input-label">Method *</label>
                            <select
                                className="cw-input"
                                value={newPayment.paymentMethod}
                                onChange={(e) => setNewPayment({ ...newPayment, paymentMethod: e.target.value })}
                                required
                            >
                                <option value="VodafoneCash">Vodafone Cash</option>
                                <option value="InstaPay">InstaPay</option>
                                <option value="Cash">Cash</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="cw-input-label">Received By *</label>
                            <select
                                className="cw-input"
                                value={newPayment.receivedBy}
                                onChange={(e) => setNewPayment({ ...newPayment, receivedBy: e.target.value })}
                                required
                            >
                                <option value="Abdullah">Abdullah</option>
                                <option value="Mohamed">Mohamed</option>
                            </select>
                        </div>

                        <div className="full-width">
                            <label className="cw-input-label">Internal Notes (Optional)</label>
                            <textarea
                                className="cw-input cw-textarea"
                                placeholder="Payment details, transaction ID, etc."
                                value={newPayment.notes}
                                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                                rows="3"
                            />
                        </div>
                    </div>

                    <div className="modal-actions-footer">
                        <ButtonSecondary onClick={() => setIsAddModalOpen(false)} type="button">Cancel</ButtonSecondary>
                        <ButtonPrimary type="submit">Verify & Save Payment</ButtonPrimary>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={isFeesModalOpen}
                onClose={() => setIsFeesModalOpen(false)}
                title="⚙️ Adjust Student Total Fees"
                size="sm"
            >
                <form onSubmit={handleUpdateFees} className="finance-modal-form">
                    <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                        Updating fees for <strong>{feesData.fullName}</strong>. This will recalculate their balance status immediately.
                    </p>
                    <Input
                        label="New Total Fees (EGP)"
                        type="number"
                        value={feesData.totalFees}
                        onChange={(e) => setFeesData({ ...feesData, totalFees: e.target.value })}
                        required
                    />
                    <div className="modal-actions-footer">
                        <ButtonSecondary onClick={() => setIsFeesModalOpen(false)} type="button">Cancel</ButtonSecondary>
                        <ButtonPrimary type="submit">Update Total Fees</ButtonPrimary>
                    </div>
                </form>
            </Modal>

            {/* Wallet Transaction Modal */}
            <Modal
                isOpen={isWalletModalOpen}
                onClose={() => {
                    setIsWalletModalOpen(false);
                    setIsEditingWallet(false);
                }}
                title={isEditingWallet ? "📝 Edit Wallet Transaction" : "💳 Record Wallet Transaction"}
                size="md"
            >
                <form onSubmit={handleWalletTransaction} className="finance-modal-form">
                    <div className="form-grid">
                        <div className="full-width">
                            <label className="cw-input-label">Collector *</label>
                            <select
                                className="cw-input"
                                value={walletForm.collectorName}
                                onChange={(e) => setWalletForm({ ...walletForm, collectorName: e.target.value })}
                                required
                            >
                                <option value="Abdullah">Abdullah</option>
                                <option value="Mohamed">Mohamed</option>
                            </select>
                        </div>
                        <div className="full-width">
                            <label className="cw-input-label">Transaction Type *</label>
                            <select
                                className="cw-input"
                                value={walletForm.type}
                                onChange={(e) => setWalletForm({ ...walletForm, type: e.target.value, category: e.target.value === 'Outgoing' ? 'Expense' : 'Other' })}
                                required
                                disabled={isEditingWallet}
                            >
                                <option value="Incoming">Incoming (Add to Wallet)</option>
                                <option value="Outgoing">Outgoing (Deduct from Wallet)</option>
                            </select>
                        </div>
                        <Input
                            label="Amount (EGP) *"
                            type="number"
                            placeholder="0.00"
                            value={walletForm.amount}
                            onChange={(e) => setWalletForm({ ...walletForm, amount: e.target.value })}
                            required
                        />
                        <div className="full-width">
                            <label className="cw-input-label">Category</label>
                            <select
                                className="cw-input"
                                value={walletForm.category}
                                onChange={(e) => setWalletForm({ ...walletForm, category: e.target.value })}
                            >
                                {walletForm.type === 'Incoming' ? (
                                    <>
                                        <option value="Other">Other Incoming</option>
                                        <option value="Refund">Refund from Expense</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="Expense">Expense / Spending</option>
                                        <option value="Transfer">Transfer to Admin</option>
                                        <option value="Other">Other Outgoing</option>
                                    </>
                                )}
                            </select>
                        </div>
                        <div className="full-width">
                            <label className="cw-input-label">Notes</label>
                            <textarea
                                className="cw-input cw-textarea"
                                placeholder="What is this for?"
                                value={walletForm.notes}
                                onChange={(e) => setWalletForm({ ...walletForm, notes: e.target.value })}
                                rows="3"
                            />
                        </div>
                    </div>
                    <div className="modal-actions-footer">
                        <ButtonSecondary onClick={() => {
                            setIsWalletModalOpen(false);
                            setIsEditingWallet(false);
                        }} type="button">Cancel</ButtonSecondary>
                        <ButtonPrimary type="submit">
                            {isEditingWallet ? "Save Changes" : "Record Transaction"}
                        </ButtonPrimary>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
}
